import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import ArrowForward from '@material-ui/icons/ArrowForward';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import FirebaseRoomService from 'services/FirebaseRoomService';
import FirebaseStore from 'stores/FirebaseStore';
import GiphyStore from 'stores/GiphyStore';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import APIStates from 'enum/APIStates';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { CHECK_ROOM_STATUS_CHANGE_FROM_PRIVATE_ROOM } from 'enum/profile';

import { FIREBASE_AUTH_STATUS } from '../../firebase/firebaseStructure';
import privateRoomLockedImg from '../../../images/privateRoomLocked.png';

import styles from './DesktopBroadcastPrivateRoom.css';


const DesktopBroadcastPrivateRoom = (props) => {
  const { user, roomName, setShowRegistrationModal } = props;
  const {
    authData,
    isPrivateRoomLoading,
    setPrivateRoomLoadingStatus,
    wrongRoomPassword,
  } = FirebaseStore;

  const [password, setPassword] = useState('');
  const [inputType, setInputType] = useState('text');

  useEffect(() => {
    const { closePopper } = GiphyStore;
    closePopper();
    FirebaseRoomService.removeWebPresence();

    return closePopper;
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const privateRoomOnLoad = authData && FirebaseStore.authData.status === FIREBASE_AUTH_STATUS.privateRoom;
    if (privateRoomOnLoad || wrongRoomPassword) {
      const checkIfRoomWentPublic = setInterval(() => {
        FirebaseStore.initRoom(roomName, user.username, user.accessHash);
      }, CHECK_ROOM_STATUS_CHANGE_FROM_PRIVATE_ROOM);

      return () => clearInterval(checkIfRoomWentPublic);
    }
  }, [user, roomName, user.username, user.accessHash, authData, wrongRoomPassword]);

  const onChangePasswordInput = useCallback((event) => {
    const { value } = event.target;
    setPassword(value);
  }, []);

  const onKeyDownPasswordInput = useCallback(() => {
    setInputType('password');
  }, []);

  const onClickGetPrivateRoomAccess = useCallback(() => {
    const { username, accessHash, accessLevel } = user;
    const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;

    if (!username) {
      if (!user.accessHash) {
        ModalService.showModal(ModalService.MODAL_KEYS.LOGIN_MODAL_V2);
      }
      setShowRegistrationModal();
    } else if (isGuestBilling) {
      const modalKey = ModalService.MODAL_KEYS.GUEST_BILLING_MODAL;
      const modalProps = {
        close: ModalService.hideModal,
        show: true,
      };

      ModalService.showModal(modalKey, modalProps);
    } else {
      setPrivateRoomLoadingStatus(APIStates.PENDING);
      FirebaseStore.initRoom(roomName, username, accessHash, password);
    }
  }, [
    password,
    roomName,
    setPrivateRoomLoadingStatus,
    setShowRegistrationModal,
    user,
  ]);

  const passwordWrapper = classNames({
    [styles.passwordWrapper]: !wrongRoomPassword,
    [styles.passwordWrapperWithOutError]: wrongRoomPassword,
  });

  return (
    <div className={styles.privateRoomWrapper}>
      <div className={styles.privateRoomInfo}>
        <div className={styles.privateLockedContent}>
          <img alt="locked" src={privateRoomLockedImg} />
          <p>
            {translate(
              'v2profile.this.is.private.broadcast',
              { 0: '<strong>', 1: '</strong>' }
            )}
          </p>
        </div>
        <div className={passwordWrapper}>
          <p>
            {translate('v2profile.please.enter.password')}
          </p>
          <div className={styles.inputWrapper}>
            {/*
             * inputType was used here like this because of autofill and autocomplete check IM-3242
             */}
            <input
              autoComplete="new-password"
              placeholder={translate('v2profile.password.placeholder')}
              type={inputType}
              value={password}
              onChange={onChangePasswordInput}
              onKeyDown={onKeyDownPasswordInput}
            />
            <LoadingButton
              colour="orange"
              isLoading={isPrivateRoomLoading}
              onClick={onClickGetPrivateRoomAccess}
            >
              <ArrowForward />
            </LoadingButton>
          </div>
          {wrongRoomPassword && (
            <div className={styles.error}>
              {translate('v2profile.invalid.password')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DesktopBroadcastPrivateRoom.propTypes = {
  roomName: PropTypes.string,
  setShowRegistrationModal: PropTypes.func,
  user: PropTypes.object,
};

DesktopBroadcastPrivateRoom.defaultProps = {
  roomName: undefined,
  setShowRegistrationModal: () => undefined,
  user: undefined,
};

export default observer(DesktopBroadcastPrivateRoom);
